import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconModule } from 'src/app/shared/icon/icon.module';

export type DeleteConfirmVariant = 'soft-delete' | 'permanent';

@Component({
  selector: 'app-delete-confirm-modal',
  standalone: true,
  imports: [CommonModule, IconModule],
  template: `
    <div
      *ngIf="open"
      class="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 px-4"
      (click)="onBackdrop()"
    >
      <div
        class="relative w-full max-w-sm rounded-2xl bg-white dark:bg-[#0e1726] shadow-xl p-6"
        (click)="$event.stopPropagation()"
      >
        <div class="mb-4 flex justify-center">
          <div class="flex h-14 w-14 items-center justify-center rounded-full bg-danger/10">
            <icon-trash-lines class="size-7 text-danger" />
          </div>
        </div>
        <h3 class="text-center text-base font-semibold text-[#18214d] dark:text-white mb-1">
          {{ heading }}
        </h3>
        <p *ngIf="message; else defaultBody" class="text-center text-sm text-gray-500 dark:text-gray-400 mb-6">
          {{ message }}
        </p>
        <ng-template #defaultBody>
          <p class="text-center text-sm text-gray-500 dark:text-gray-400 mb-6">
            <ng-container *ngIf="variant === 'soft-delete'">
              This will soft-delete <strong>{{ entityName }}</strong>. The {{ entityNoun }} moves to the deleted list and
              can be restored later.
            </ng-container>
            <ng-container *ngIf="variant === 'permanent'">
              Are you sure you want to delete <strong>{{ entityName }}</strong>? This action cannot be undone.
            </ng-container>
          </p>
        </ng-template>
        <div
          *ngIf="error"
          class="mb-4 rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger"
          role="alert"
        >
          {{ error }}
        </div>
        <div class="flex gap-3">
          <button
            type="button"
            (click)="onCancel()"
            class="flex-1 rounded-lg border border-gray-200 dark:border-[#191e3a] py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            type="button"
            (click)="onConfirm()"
            [disabled]="loading"
            class="flex-1 rounded-lg bg-danger py-2 text-sm font-medium text-white hover:bg-danger/90 disabled:opacity-60"
          >
            <span *ngIf="!loading">{{ confirmLabel }}</span>
            <span *ngIf="loading">{{ loadingLabel }}</span>
          </button>
        </div>
      </div>
    </div>
  `,
})
export class DeleteConfirmModalComponent {
  @Input() open = false;
  @Input() heading = 'Delete?';
  @Input() entityName = '';
  /** Noun used in soft-delete copy, e.g. "user", "client", "role". */
  @Input() entityNoun = 'record';
  @Input() message = '';
  @Input() variant: DeleteConfirmVariant = 'soft-delete';
  @Input() confirmLabel = 'Remove';
  @Input() loadingLabel = 'Removing…';
  @Input() loading = false;
  @Input() error = '';

  @Output() cancelled = new EventEmitter<void>();
  @Output() confirmed = new EventEmitter<void>();

  onBackdrop(): void {
    this.cancelled.emit();
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  onConfirm(): void {
    this.confirmed.emit();
  }
}
